import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminService } from '../../services/adminService';
import { CloudProvider, OnboardingStatus } from '../../types/admin';
import { LoadingState } from '../common/LoadingState';
import { ErrorState } from '../common/ErrorState';
import { CloudProviderIcon } from '../common/CloudProviderIcon';
import { getProviderBackgroundTint } from '../../utils/providerColors';
import { Button } from '../common/Button';
import { IconButton } from '../common/IconButton';

interface FieldDef {
  key: string;
  label: string;
  placeholder: string;
  type?: 'text' | 'password' | 'checkbox';
  required?: boolean;
  // Shown under a checkbox to explain what ticking it changes.
  help?: string;
}

// Mirrors AWSIAMRoleConfig / AzureServicePrincipalConfig / GCPWIFConfig
// (types/admin.ts) field-for-field - AWS role assumption and GCP Workload
// Identity Federation are both keyless by design, so clientSecret is the
// only real secret collected here, and it goes to Key Vault, never Postgres.
const FIELD_DEFS: Record<CloudProvider, FieldDef[]> = {
  [CloudProvider.AWS]: [
    { key: 'accountId', label: 'AWS Account ID', placeholder: '123456789012', required: true },
    { key: 'roleName', label: 'IAM Role Name', placeholder: 'FinOpsPlatformRole', required: true },
    // Not referenced by the generated trust policy - this platform federates
    // via Entra ID OIDC (sts:AssumeRoleWithWebIdentity), not the classic
    // sts:AssumeRole + sts:ExternalId pattern. The label says so rather than
    // implying it is a security control the customer must get right.
    { key: 'externalId', label: 'External ID (not used by AWS - any value)', placeholder: 'e.g. not-used', required: true },
    { key: 'curBucketName', label: 'CUR S3 Bucket Name', placeholder: 'my-cur-export-bucket', required: true },
    { key: 'curBucketRegion', label: 'CUR Bucket Region', placeholder: 'us-east-1', required: true },
    { key: 'curPrefix', label: 'CUR Prefix (optional)', placeholder: 'cur-reports/' },
    {
      key: 'isOrganizationManagementAccount',
      label: 'This is my AWS Organizations management (payer) account',
      placeholder: '',
      type: 'checkbox',
      help: 'Adds organizations:ListAccounts to the generated role so "Find Siblings" can discover your member accounts. Leave unchecked for a member or standalone account - the permission only returns results from the management account anyway.',
    },
    {
      key: 'allowPlatformManagedStackSets',
      label: 'Let the platform deploy roles to my member accounts (optional)',
      placeholder: '',
      type: 'checkbox',
      help:
        'Adds CloudFormation StackSets permissions so "Deploy automatically" can create the read-only role across your member accounts for you, instead of you running a StackSet by hand. ' +
        'This is the only permission here that lets the platform create resources rather than just read billing data - leave it unchecked if you would rather run that one step yourself. Requires the payer-account box above. ' +
        // Surfaced here rather than only in an error after the fact: this is a
        // prerequisite AWS requires a human to set, the platform cannot set it,
        // and hitting it as a failure later reads like the feature is broken.
        'One-time AWS setup this needs: StackSets trusted access must be enabled in your AWS Organization (AWS Console → CloudFormation → StackSets, accept the prompt). You only ever do this once per organization - not per account.',
    },
  ],
  [CloudProvider.AZURE]: [
    { key: 'tenantId', label: 'Azure AD Tenant ID', placeholder: '00000000-0000-0000-0000-000000000000', required: true },
    { key: 'subscriptionId', label: 'Subscription ID', placeholder: '00000000-0000-0000-0000-000000000000', required: true },
    { key: 'clientId', label: 'App Registration Client ID', placeholder: '00000000-0000-0000-0000-000000000000', required: true },
    { key: 'clientSecret', label: 'Client Secret', placeholder: 'stored in Key Vault - never shown again after saving', type: 'password', required: true },
    { key: 'storageAccountName', label: 'Cost Export Storage Account', placeholder: 'mycostexports', required: true },
    { key: 'containerName', label: 'Container Name', placeholder: 'azure-cost-export', required: true },
  ],
  [CloudProvider.GCP]: [
    { key: 'projectId', label: 'GCP Project ID', placeholder: 'my-project-123', required: true },
    { key: 'workloadIdentityPoolId', label: 'Workload Identity Pool ID', placeholder: 'finops-pool', required: true },
    { key: 'workloadIdentityProviderId', label: 'Workload Identity Provider — full resource path (not the short name)', placeholder: '//iam.googleapis.com/projects/<number>/locations/global/workloadIdentityPools/<pool>/providers/<provider>', required: true },
    { key: 'serviceAccountEmail', label: 'Service Account Email', placeholder: 'finops@my-project.iam.gserviceaccount.com', required: true },
    { key: 'billingExportDataset', label: 'Billing Export Dataset', placeholder: 'billing_export', required: true },
    { key: 'billingExportTable', label: 'Billing Export Table', placeholder: 'gcp_billing_export_v1', required: true },
  ],
};

const WIZARD_COPY: Record<CloudProvider, string> = {
  [CloudProvider.AWS]: 'Configure a cross-account IAM role for CUR ingestion. No access keys needed - just a role and an external ID.',
  [CloudProvider.AZURE]: 'Configure a service principal with Cost Management + Storage Blob read access.',
  [CloudProvider.GCP]: 'Configure keyless Workload Identity Federation for BigQuery billing export access - no service account key file is ever created.',
};

type ArtifactTabKey = 'cloudformation' | 'terraform' | 'script' | 'policy';

/**
 * Small tabbed code viewer with a copy button - one tab per generated
 * onboarding artifact. Only tabs with real content are shown - every
 * provider now has at least one runnable artifact tab (AWS:
 * CloudFormation+Terraform, Azure: CLI script, GCP: Terraform+CLI script)
 * alongside the always-present Policy JSON tab.
 */
const ArtifactCodeViewer: React.FC<{
  cloudFormationTemplate?: string;
  terraformSnippet?: string;
  onboardingScript?: string;
  // Optional so a caller with only one artifact (e.g. the member StackSet,
  // which is CloudFormation and has no separate policy JSON) doesn't get an
  // empty tab mislabelled "Policy JSON".
  policyDocument?: string;
}> = ({ cloudFormationTemplate, terraformSnippet, onboardingScript, policyDocument }) => {
  const tabs: { key: ArtifactTabKey; label: string; content: string }[] = [
    ...(cloudFormationTemplate ? [{ key: 'cloudformation' as const, label: 'CloudFormation', content: cloudFormationTemplate }] : []),
    ...(terraformSnippet ? [{ key: 'terraform' as const, label: 'Terraform', content: terraformSnippet }] : []),
    ...(onboardingScript ? [{ key: 'script' as const, label: 'CLI Script', content: onboardingScript }] : []),
    ...(policyDocument ? [{ key: 'policy' as const, label: 'Policy JSON', content: policyDocument }] : []),
  ];
  // Every prop is optional now, so an all-empty call would leave `tabs` empty
  // and `tabs[0].key` would throw. Default the key instead of crashing the
  // whole Admin Console over a missing artifact.
  const [activeTab, setActiveTab] = useState<ArtifactTabKey>(tabs[0]?.key ?? 'policy');
  const [copied, setCopied] = useState(false);
  const active = tabs.find((t) => t.key === activeTab) || tabs[0];

  if (tabs.length === 0) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(active.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API can be unavailable (non-HTTPS, permissions) - fail
      // silently rather than showing a scary error for a copy convenience.
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between border-b border-border">
        <div className="flex gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px ${
                activeTab === tab.key
                  ? 'border-primary/30 text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground/80 dark:text-muted-foreground/70 dark:hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <button
          onClick={handleCopy}
          className="mb-1 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-secondary dark:text-muted-foreground dark:hover:bg-secondary"
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <pre className="mt-1 max-h-64 overflow-auto rounded-md border border-border bg-muted/30 p-3 text-xs text-foreground dark:border-border dark:bg-card dark:text-foreground">
{active.content}
      </pre>
      {active.content.includes('<FINOPS_PLATFORM_ACCOUNT_ID>') && (
        <p className="mt-1 text-xs text-muted-foreground">
          Deploy this in the account being onboarded, then replace <code>&lt;FINOPS_PLATFORM_ACCOUNT_ID&gt;</code> with this
          platform's real AWS account ID before applying.
        </p>
      )}
      {(activeTab === 'script') && (
        <p className="mt-1 text-xs text-muted-foreground">
          Run this in the account/subscription/project being onboarded - it only grants access to an identity you've already created (the client ID / service account entered above), it never creates a new one.
        </p>
      )}
    </div>
  );
};

/**
 * Cloud Onboarding Tab
 * Implements Requirement 82 AC 1 / Requirement 119: real cloud account
 * onboarding, backed by a real tenant-scoped table and Azure Key Vault for
 * secrets (see cloud-account.service.ts). Submitting an account now runs a
 * real connection check for all three providers (Requirement 119 AC2) -
 * new accounts start at "pending verification" and flip to "completed" or
 * "failed" once that check runs, a few seconds after submit. Still NOT
 * covered: triggering a real historical backfill on first connect
 * (Requirement 119 AC4) - data just starts flowing live from whenever the
 * account is verified.
 */
export const CloudOnboardingTab: React.FC = () => {
  const [showWizard, setShowWizard] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<CloudProvider | null>(null);
  const [accountName, setAccountName] = useState('');
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [policyDocument, setPolicyDocument] = useState<string | null>(null);
  const [cloudFormationTemplate, setCloudFormationTemplate] = useState<string | undefined>(undefined);
  const [terraformSnippet, setTerraformSnippet] = useState<string | undefined>(undefined);
  const [onboardingScript, setOnboardingScript] = useState<string | undefined>(undefined);
  const [manifestInfo, setManifestInfo] = useState<{ signed: boolean; hash?: string; signedAt?: string } | null>(null);
  const [overlapWarnings, setOverlapWarnings] = useState<string[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [verifyingId, setVerifyingId] = useState<string | null>(null);
  const [teardownScript, setTeardownScript] = useState<string | null>(null);
  const [teardownCopied, setTeardownCopied] = useState(false);
  const [preflightResult, setPreflightResult] = useState<{ ok: boolean; notAvailable: boolean; results: { action: string; allowed: boolean; label?: string }[]; message: string } | null>(null);
  const [preflightChecking, setPreflightChecking] = useState(false);
  const [hierarchyModal, setHierarchyModal] = useState<{ accountId: string; notConfigured: boolean; message?: string; accounts: { provider: string; accountId: string; name: string; parentId?: string }[] } | null>(null);
  const [discoveringId, setDiscoveringId] = useState<string | null>(null);
  // 'awaiting-role' is a real, distinct outcome from 'done' - the record was
  // created, but the account has no cross-account role yet, so it will not
  // work until one is deployed. Previously this state didn't exist: a
  // successful POST was shown as 'done' with a green "Onboarded" badge
  // regardless of whether the account could actually be used, which is
  // exactly backwards for a customer who has no idea the role still needs
  // deploying somewhere else.
  const [siblingOnboardState, setSiblingOnboardState] = useState<Record<string, 'onboarding' | 'done' | 'awaiting-role' | 'error'>>({});
  // Per-sibling generated artifacts, captured from the same response the main
  // "Add Cloud Account" wizard already shows a template from - previously
  // discarded entirely for a sibling, leaving no in-product way to get a
  // deployable template for that specific account without external help.
  const [siblingArtifacts, setSiblingArtifacts] = useState<
    Record<string, { cloudFormationTemplate?: string; terraformSnippet?: string; onboardingScript?: string; policyDocument: string; cloudAccountId: string }>
  >({});
  const [siblingVerifying, setSiblingVerifying] = useState<Record<string, boolean>>({});
  // FR-ONB-005 Phase 1: the one StackSet covering every member account, so the
  // customer runs a single CloudFormation action in the management account
  // rather than one per member account.
  const [stackSetTemplate, setStackSetTemplate] = useState<string | null>(null);
  const [stackSetLoading, setStackSetLoading] = useState(false);
  const [stackSetError, setStackSetError] = useState<string | null>(null);
  // FR-ONB-005 Phase 2 - platform-run deployment state.
  const [autoDeploying, setAutoDeploying] = useState(false);
  const [autoDeployMessage, setAutoDeployMessage] = useState<{ text: string; kind: 'info' | 'error' | 'setup' } | null>(null);
  const [autoDeployProgress, setAutoDeployProgress] = useState<{
    status: string;
    instances: { accountId: string; status: string; statusReason?: string }[];
  } | null>(null);
  const [siblingErrors, setSiblingErrors] = useState<Record<string, string>>({});
  const [enablingOptimizationId, setEnablingOptimizationId] = useState<string | null>(null);
  const [optimizationResult, setOptimizationResult] = useState<{ computeOptimizer: { enrolled: boolean; available: boolean; message: string }; trustedAdvisor: { available: boolean; message: string } } | null>(null);

  const {
    data: accounts,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['cloudAccounts'],
    queryFn: () => adminService.getCloudAccounts(),
    // Backfill (Requirement 119 AC4) runs in the background after
    // verification succeeds - poll so "in progress" flips to "complete" on
    // its own instead of requiring another manual "Verify Now" click.
    refetchInterval: 5000,
  });

  const resetWizard = () => {
    setShowWizard(false);
    setSelectedProvider(null);
    setAccountName('');
    setFieldValues({});
    setSubmitError(null);
    setPolicyDocument(null);
    setCloudFormationTemplate(undefined);
    setTerraformSnippet(undefined);
    setOnboardingScript(undefined);
    setManifestInfo(null);
    setPreflightResult(null);
    setOverlapWarnings([]);
  };

  const handleCheckPreflight = async () => {
    if (!selectedProvider) return;
    setPreflightChecking(true);
    setPreflightResult(null);
    try {
      const result = await adminService.checkOnboardingPreflight(selectedProvider, fieldValues);
      setPreflightResult(result);
    } catch (err: any) {
      setPreflightResult({ ok: false, notAvailable: true, results: [], message: err?.response?.data?.error || err?.message || 'Preflight check failed' });
    } finally {
      setPreflightChecking(false);
    }
  };

  const handleDiscoverHierarchy = async (accountId: string) => {
    setDiscoveringId(accountId);
    setSiblingOnboardState({});
    setSiblingErrors({});
    try {
      const result = await adminService.discoverCloudAccountHierarchy(accountId);
      // Multi-account onboarding fix: pre-mark siblings this tenant has
      // already onboarded as "done" up front, instead of letting the user
      // click "Onboard" and hit an "already exists" error for something
      // that was never actually a failure.
      if (result.alreadyOnboarded && result.alreadyOnboarded.length > 0) {
        setSiblingOnboardState(
          result.alreadyOnboarded.reduce((acc, id) => ({ ...acc, [id]: 'done' as const }), {})
        );
      }
      setHierarchyModal({ accountId, ...result });
    } catch (err: any) {
      // eslint-disable-next-line no-alert
      alert(err?.response?.data?.error || err?.message || 'Failed to discover account hierarchy');
    } finally {
      setDiscoveringId(null);
    }
  };

  /**
   * Onboards a discovered sibling with the SAME role name / external ID /
   * CUR bucket settings as the parent account it was discovered from - the
   * only thing that actually differs per AWS sibling account is the account
   * ID itself, so this removes the "re-open the wizard and retype every
   * field" friction for a real multi-account org, without pretending this
   * is StackSets-style automated provisioning (the sibling still needs the
   * cross-account role deployed in its own account first - same trust model
   * as any other onboarding here).
   */
  const handleOnboardSibling = async (sibling: { provider: string; accountId: string; name: string }) => {
    if (!hierarchyModal) return;
    const parent = accounts?.find((a: any) => a.id === hierarchyModal.accountId);
    if (!parent) return;

    setSiblingOnboardState((prev) => ({ ...prev, [sibling.accountId]: 'onboarding' }));
    setSiblingErrors((prev) => ({ ...prev, [sibling.accountId]: '' }));
    try {
      const config = { ...(parent.config || {}), accountId: sibling.accountId };
      // Multi-account onboarding fix: marks this new row 'member' and the
      // parent account 'master' server-side (see cloud-account.service.ts's
      // createCloudAccount) - this is what makes "Find Siblings" + "Onboard"
      // actually a master+sub-account relationship, not just two unrelated
      // standalone rows.
      const result = await adminService.createCloudAccount(
        parent.provider,
        sibling.name || `${parent.accountName} - ${sibling.accountId}`,
        config,
        { accountType: 'member', parentCloudAccountId: parent.id }
      );

      // A successful POST only means the record was created - it does NOT
      // mean the sibling account can actually be reached yet, since the
      // config was copied from the parent and this account has never had a
      // role deployed into it. Reflect the real status rather than declaring
      // victory on request success alone.
      if (result.account?.status === 'completed') {
        setSiblingOnboardState((prev) => ({ ...prev, [sibling.accountId]: 'done' }));
      } else {
        setSiblingOnboardState((prev) => ({ ...prev, [sibling.accountId]: 'awaiting-role' }));
        setSiblingArtifacts((prev) => ({
          ...prev,
          [sibling.accountId]: {
            cloudFormationTemplate: result.cloudFormationTemplate,
            terraformSnippet: result.terraformSnippet,
            onboardingScript: result.onboardingScript,
            policyDocument: result.policyDocument,
            cloudAccountId: result.account.id,
          },
        }));
      }
      refetch();
    } catch (err: any) {
      setSiblingOnboardState((prev) => ({ ...prev, [sibling.accountId]: 'error' }));
      setSiblingErrors((prev) => ({ ...prev, [sibling.accountId]: err?.response?.data?.error || err?.message || 'Failed to onboard' }));
    }
  };

  const handleLoadStackSet = async () => {
    if (!hierarchyModal) return;
    setStackSetLoading(true);
    setStackSetError(null);
    try {
      const { stackSetTemplate: tpl } = await adminService.getMemberStackSetTemplate(hierarchyModal.accountId);
      setStackSetTemplate(tpl);
    } catch (err: any) {
      setStackSetError(err?.response?.data?.error || err?.message || 'Could not generate the StackSet template');
    } finally {
      setStackSetLoading(false);
    }
  };

  /**
   * FR-ONB-005 Phase 2: ask the platform to run the StackSet itself, then poll
   * until AWS reports the operation finished. Polling is bounded rather than
   * indefinite so a stuck operation surfaces as "still running - check the AWS
   * console" instead of spinning forever with no explanation.
   */
  const handleAutoDeploy = async () => {
    if (!hierarchyModal) return;
    setAutoDeploying(true);
    setAutoDeployMessage(null);
    setAutoDeployProgress(null);
    try {
      const result = await adminService.deployMemberStackSet(hierarchyModal.accountId);
      if (!result.ok || !result.operationId) {
        setAutoDeployMessage({ text: result.message, kind: result.needsSetup ? 'setup' : 'error' });
        return;
      }

      setAutoDeployMessage({ text: 'Deployment started across your organization…', kind: 'info' });

      const MAX_POLLS = 40; // ~4 minutes at 6s
      for (let i = 0; i < MAX_POLLS; i++) {
        await new Promise((r) => setTimeout(r, 6000));
        try {
          const status = await adminService.getStackSetOperationStatus(hierarchyModal.accountId, result.operationId);
          setAutoDeployProgress(status);
          if (['SUCCEEDED', 'FAILED', 'STOPPED'].includes(status.status)) {
            setAutoDeployMessage(
              status.status === 'SUCCEEDED'
                ? { text: 'Roles deployed. Use Onboard all below - each account should verify immediately now.', kind: 'info' }
                : { text: `AWS reported the deployment ${status.status.toLowerCase()}. Per-account detail is below.`, kind: 'error' }
            );
            refetch();
            return;
          }
        } catch {
          // A single failed poll shouldn't abort the whole wait - the
          // operation is running in AWS regardless of our ability to read it.
        }
      }
      setAutoDeployMessage({
        text: 'Still running after several minutes. It is safe to close this - check CloudFormation → StackSets in the AWS console for the final result.',
        kind: 'info',
      });
    } catch (err: any) {
      setAutoDeployMessage({
        text: err?.response?.data?.error || err?.message || 'Could not start the deployment',
        kind: 'error',
      });
    } finally {
      setAutoDeploying(false);
    }
  };

  const handleVerifySibling = async (sibling: { accountId: string }) => {
    const artifacts = siblingArtifacts[sibling.accountId];
    if (!artifacts) return;
    setSiblingVerifying((prev) => ({ ...prev, [sibling.accountId]: true }));
    try {
      const { account: updated } = await adminService.verifyCloudAccount(artifacts.cloudAccountId);
      refetch();
      // Check this specific account's real returned status rather than
      // assuming success, since verification can complete without the
      // account actually reaching 'completed' (e.g. role deployed but CUR
      // hasn't landed in S3 yet).
      if (updated?.status === 'completed') {
        setSiblingOnboardState((prev) => ({ ...prev, [sibling.accountId]: 'done' }));
      } else {
        setSiblingErrors((prev) => ({
          ...prev,
          [sibling.accountId]: updated?.verificationError || 'Still not verified - check that the role was deployed into this exact account.',
        }));
      }
    } catch (err: any) {
      setSiblingErrors((prev) => ({ ...prev, [sibling.accountId]: err?.response?.data?.error || err?.message || 'Verification failed' }));
    } finally {
      setSiblingVerifying((prev) => ({ ...prev, [sibling.accountId]: false }));
    }
  };

  /**
   * Bulk "Onboard all" (Requirement AC-ONB-002's spirit, scoped to what this
   * platform can safely automate today - see the plan's decision to not
   * build full StackSets-style automated cross-account role deployment):
   * loops the same per-sibling onboarding call above over every undone
   * sibling, in small concurrent batches so a large org doesn't hammer
   * createCloudAccount's preflight+verify+manifest chain all at once. Each
   * sibling still only succeeds if its own cross-account role already
   * exists - this automates the repetitive submission, not the underlying
   * cloud-side provisioning.
   */
  const handleOnboardAllSiblings = async () => {
    if (!hierarchyModal) return;
    // 'awaiting-role' already has a real record - re-onboarding it would just
    // hit a duplicate-account error, so it's excluded the same as 'done'.
    const todo = hierarchyModal.accounts.filter(
      (a) => siblingOnboardState[a.accountId] !== 'done' && siblingOnboardState[a.accountId] !== 'awaiting-role'
    );
    const BATCH_SIZE = 4;
    for (let i = 0; i < todo.length; i += BATCH_SIZE) {
      const batch = todo.slice(i, i + BATCH_SIZE);
      await Promise.all(batch.map((sibling) => handleOnboardSibling(sibling)));
    }
  };

  const handleSubmit = async () => {
    if (!selectedProvider) return;
    const missing = FIELD_DEFS[selectedProvider].filter((f) => f.required && !fieldValues[f.key]?.trim());
    if (!accountName.trim() || missing.length > 0) {
      setSubmitError(
        !accountName.trim()
          ? 'Account Name is required'
          : `Missing required field(s): ${missing.map((f) => f.label).join(', ')}`
      );
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      // Checkbox fields are held as 'true'/'' strings by the shared text-input
      // state above; convert them to real booleans so the backend's config
      // types (e.g. AWSIAMRoleConfig.isOrganizationManagementAccount) receive a
      // boolean rather than a truthy string.
      const checkboxKeys = FIELD_DEFS[selectedProvider].filter((f) => f.type === 'checkbox').map((f) => f.key);
      const payload: Record<string, any> = { ...fieldValues };
      for (const key of checkboxKeys) {
        payload[key] = fieldValues[key] === 'true';
      }

      const result = await adminService.createCloudAccount(selectedProvider, accountName.trim(), payload);
      setPolicyDocument(result.policyDocument);
      setCloudFormationTemplate(result.cloudFormationTemplate);
      setTerraformSnippet(result.terraformSnippet);
      setOnboardingScript(result.onboardingScript);
      setManifestInfo({ signed: result.manifestSigned, hash: result.manifestHash, signedAt: result.manifestSignedAt });
      setOverlapWarnings(result.warnings || []);
      refetch();
    } catch (err: any) {
      setSubmitError(err?.response?.data?.error || err?.message || 'Failed to onboard cloud account');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async (id: string) => {
    setVerifyingId(id);
    try {
      await adminService.verifyCloudAccount(id);
      refetch();
    } catch (err: any) {
      // eslint-disable-next-line no-alert
      alert(err?.response?.data?.error || err?.message || 'Failed to verify cloud account');
    } finally {
      setVerifyingId(null);
    }
  };

  const handleEnableOptimization = async (id: string) => {
    setEnablingOptimizationId(id);
    setOptimizationResult(null);
    try {
      const result = await adminService.enableAwsOptimization(id);
      setOptimizationResult(result);
    } catch (err: any) {
      // eslint-disable-next-line no-alert
      alert(err?.response?.data?.error || err?.message || 'Failed to enable AWS optimization services');
    } finally {
      setEnablingOptimizationId(null);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      const result = await adminService.deleteCloudAccount(id);
      if (result.teardownScript) {
        setTeardownScript(result.teardownScript);
      }
      refetch();
    } catch (err: any) {
      // eslint-disable-next-line no-alert
      alert(err?.response?.data?.error || err?.message || 'Failed to delete cloud account');
    } finally {
      setDeletingId(null);
    }
  };

  const getStatusBadge = (status: OnboardingStatus) => {
    const styles: Record<OnboardingStatus, string> = {
      [OnboardingStatus.COMPLETED]: 'bg-emerald-500/10 text-emerald-600 dark:bg-emerald-900 dark:text-emerald-200',
      [OnboardingStatus.IN_PROGRESS]: 'bg-amber-500/10 text-amber-600 dark:bg-amber-900 dark:text-amber-200',
      [OnboardingStatus.FAILED]: 'bg-rose-500/10 text-rose-600 dark:bg-rose-900 dark:text-rose-200',
      [OnboardingStatus.NOT_STARTED]: 'bg-secondary text-foreground dark:bg-secondary dark:text-muted-foreground',
      [OnboardingStatus.PENDING_VERIFICATION]: 'bg-sky-500/10 text-sky-600 dark:bg-primary/10 dark:text-primary',
    };

    return (
      <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${styles[status] || styles[OnboardingStatus.NOT_STARTED]}`}>
        {status.replace(/_/g, ' ').toUpperCase()}
      </span>
    );
  };


  if (isLoading) {
    return <LoadingState message="Loading cloud accounts..." />;
  }

  if (error) {
    return (
      <ErrorState
        title="Failed to load cloud accounts"
        message="Unable to fetch cloud account data. Please try again."
        onRetry={refetch}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Add Account Button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-foreground">Cloud Accounts</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Configure cloud account connections for cost data ingestion
          </p>
        </div>
        <button
          onClick={() => setShowWizard(true)}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Cloud Account
        </button>
      </div>

      {/* Cloud Accounts List */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {accounts?.map((account: any) => (
          <div
            key={account.id}
            className="rounded-lg border border-border bg-card p-4 shadow-sm dark:border-border dark:bg-card"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-3">
                <span
                  className="flex h-9 w-10 shrink-0 items-center justify-center rounded-full"
                  style={{ backgroundColor: getProviderBackgroundTint(account.provider) }}
                >
                  <CloudProviderIcon provider={account.provider} size={22} />
                </span>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-foreground">
                      {account.accountName}
                    </h3>
                    {account.accountType === 'master' && (
                      <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-800 dark:bg-sky-900 dark:text-sky-200" title="Sibling accounts have been onboarded under this one">
                        Master
                      </span>
                    )}
                    {account.accountType === 'member' && (
                      <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-800 dark:bg-sky-900 dark:text-sky-200" title="Onboarded as a sibling of another account">
                        Member
                      </span>
                    )}
                    {account.hasStoredSecret && (
                      <span title="Secret stored in Key Vault">
                        <svg className="h-3.5 w-3.5 text-muted-foreground/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{account.accountId}</p>
                </div>
              </div>
              {/* Same reason as the action row below: nothing to remove. */}
              {!(account as any).isSynthetic && (
              <IconButton
                onClick={() => handleDelete(account.id)}
                disabled={deletingId === account.id}
                aria-label="Remove account"
                title="Remove account"
                className="text-rose-500 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-900/20"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </IconButton>
              )}
            </div>

            <div className="mt-3 space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Status:</span>
                {getStatusBadge(account.status)}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Provider:</span>
                <span className="text-sm font-medium text-foreground">
                  {account.provider.toUpperCase()}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Added:</span>
                <span className="text-sm text-foreground">
                  {new Date(account.createdAt).toLocaleDateString()}
                </span>
              </div>
              {account.lastVerifiedAt && (
                <div className="text-xs text-muted-foreground">
                  Last checked: {new Date(account.lastVerifiedAt).toLocaleString()}
                </div>
              )}
              {account.verificationError && (
                <div className="rounded-md bg-amber-50 p-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                  {account.verificationError}
                </div>
              )}
              {account.preflightWarnings && account.preflightWarnings.length > 0 && (
                <div className="rounded-md bg-amber-50 p-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                  <div className="font-semibold">
                    Onboarded with {account.preflightWarnings.length} missing permission
                    {account.preflightWarnings.length === 1 ? '' : 's'}
                  </div>
                  <div className="mt-1">{account.preflightWarnings.join(', ')}</div>
                  <div className="mt-1 opacity-80">
                    Cost data is unaffected. Azure metrics and ARM cost queries stay unavailable until these are
                    granted — then run Verify Now to clear this.
                  </div>
                </div>
              )}
              {account.backfillStatus === 'in_progress' && (
                <div className="text-xs text-primary">Backfilling historical data…</div>
              )}
              {account.backfillStatus === 'completed' && (
                <div className="text-xs text-muted-foreground">
                  History available{account.backfillEarliestDate ? ` back to ${new Date(account.backfillEarliestDate).toLocaleDateString()}` : ''}
                  {account.backfillRowCount != null ? ` · ${account.backfillRowCount.toLocaleString()} rows` : ''}
                </div>
              )}
              {account.backfillStatus === 'failed' && (
                <div className="text-xs text-amber-600 dark:text-amber-400">Historical backfill scan failed - account still works, just no known history range yet</div>
              )}
            </div>

            {expandedId === account.id && (
              <div className="mt-3 rounded-md border border-border bg-muted/30 p-3 text-xs dark:border-border dark:bg-card">
                {Object.entries(account.config || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-2 py-0.5">
                    <span className="text-muted-foreground">{k}</span>
                    <span className="truncate text-foreground">{String(v)}</span>
                  </div>
                ))}
              </div>
            )}

            {/* A demo tenant's accounts are generated, not onboarded: there is
                no row to delete, no credential to verify and no organisation to
                discover siblings in. Showing those buttons anyway would mean an
                action that appears to work and silently does nothing - during a
                demo, in front of an audience. So say what these are instead. */}
            {(account as any).isSynthetic ? (
              <p className="mt-3 rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                Demo dataset account — spend, tagging and recommendations for it are generated,
                so there is no connection to verify or remove. Budgets set on it in
                <span className="font-medium"> Budget Settings</span> work normally.
              </p>
            ) : (
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setExpandedId(expandedId === account.id ? null : account.id)}
              >
                {expandedId === account.id ? 'Hide Details' : 'View Details'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={verifyingId === account.id}
                title="Re-check this account's connection"
                onClick={() => handleVerify(account.id)}
              >
                {verifyingId === account.id ? '…' : 'Verify Now'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className={account.provider === CloudProvider.AWS ? '' : 'col-span-2'}
                disabled={discoveringId === account.id}
                title="Check for sibling accounts under the same org/management group/folder"
                onClick={() => handleDiscoverHierarchy(account.id)}
              >
                {discoveringId === account.id ? '…' : 'Find Siblings'}
              </Button>
              {account.provider === CloudProvider.AWS && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={enablingOptimizationId === account.id}
                  title="Enroll in AWS Compute Optimizer and check Trusted Advisor access"
                  onClick={() => handleEnableOptimization(account.id)}
                >
                  {enablingOptimizationId === account.id ? '…' : 'Enable Optimizer'}
                </Button>
              )}
            </div>
            )}
          </div>
        ))}
      </div>

      {/* Offboarding Teardown Script Modal (Requirement 119/172) */}
      {teardownScript && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-card p-4 shadow-xl dark:bg-card">
            <div className="flex items-center justify-between border-b border-border pb-4 dark:border-border">
              <h3 className="text-sm font-semibold text-foreground">Account Removed - Teardown Script</h3>
              <button
                onClick={() => setTeardownScript(null)}
                className="text-muted-foreground/70 hover:text-muted-foreground dark:hover:text-foreground"
              >
                ✕
              </button>
            </div>
            <p className="mt-3 text-sm text-muted-foreground">
              The account record here has been removed, but nothing was changed in the customer's own AWS/Azure/GCP
              account. Run this script there to revoke the access/role/export that onboarding created.
            </p>
            <div className="mt-3 flex items-center justify-end">
              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(teardownScript);
                    setTeardownCopied(true);
                    setTimeout(() => setTeardownCopied(false), 2000);
                  } catch {
                    // Clipboard API can be unavailable - fail silently, it's a convenience action.
                  }
                }}
                className="rounded-md px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-secondary dark:text-muted-foreground dark:hover:bg-secondary"
              >
                {teardownCopied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <pre className="mt-1 max-h-96 overflow-auto rounded-md border border-border bg-muted/30 p-3 text-xs text-foreground dark:border-border dark:bg-card dark:text-foreground">
{teardownScript}
            </pre>
            <button
              onClick={() => setTeardownScript(null)}
              className="mt-4 w-full rounded-md border border-border bg-card px-3 py-2 text-sm font-medium text-foreground/80 hover:bg-muted/30 dark:border-border dark:bg-secondary dark:text-muted-foreground dark:hover:bg-secondary"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* AWS Optimization Enrollment Result Modal (Requirement 36 / FR-ONB-011/012) */}
      {optimizationResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-card p-4 shadow-xl dark:bg-card">
            <div className="flex items-center justify-between border-b border-border pb-4 dark:border-border">
              <h3 className="text-sm font-semibold text-foreground">AWS Optimization Services</h3>
              <button onClick={() => setOptimizationResult(null)} className="text-muted-foreground/70 hover:text-muted-foreground dark:hover:text-foreground">✕</button>
            </div>
            <div className="mt-4 space-y-3">
              <div className={`rounded-md p-3 text-sm ${optimizationResult.computeOptimizer.available ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-200' : 'bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-200'}`}>
                <div className="font-medium">Compute Optimizer</div>
                <div className="mt-1">{optimizationResult.computeOptimizer.message}</div>
              </div>
              <div className={`rounded-md p-3 text-sm ${optimizationResult.trustedAdvisor.available ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-200' : 'bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-200'}`}>
                <div className="font-medium">Trusted Advisor</div>
                <div className="mt-1">{optimizationResult.trustedAdvisor.message}</div>
              </div>
            </div>
            <button
              onClick={() => setOptimizationResult(null)}
              className="mt-4 w-full rounded-md border border-border bg-card px-3 py-2 text-sm font-medium text-foreground/80 hover:bg-muted/30 dark:border-border dark:bg-secondary dark:text-muted-foreground dark:hover:bg-secondary"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Sibling-Account Discovery Modal (Requirement 119/172) */}
      {hierarchyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-card p-4 shadow-xl dark:bg-card">
            <div className="flex items-center justify-between border-b border-border pb-4 dark:border-border">
              <h3 className="text-sm font-semibold text-foreground">Sibling Accounts</h3>
              <button onClick={() => setHierarchyModal(null)} className="text-muted-foreground/70 hover:text-muted-foreground dark:hover:text-foreground">✕</button>
            </div>
            {hierarchyModal.notConfigured ? (
              <p className="mt-4 text-sm text-muted-foreground">{hierarchyModal.message}</p>
            ) : hierarchyModal.accounts.length === 0 ? (
              <p className="mt-4 text-sm text-muted-foreground">No sibling accounts found under the same org/management group/folder.</p>
            ) : (
              <div className="mt-4 space-y-2">
                {/* FR-ONB-005 Phase 1. Before this, the only path was a
                    separate per-account template applied inside each member
                    account by hand - which for a real org means N console
                    sessions, so in practice member accounts never got
                    onboarded at all. One StackSet, run once here in the
                    management account, creates the role everywhere. */}
                <div className="rounded-md border border-primary/30 bg-primary/10 p-3 dark:border-primary/30 dark:bg-primary/10">
                  <p className="text-sm font-medium text-primary">
                    Deploy the role to every member account at once
                  </p>
                  <p className="mt-1 text-xs text-primary">
                    Run this once from the management account - AWS Organizations creates the role in every member
                    account for you. You don't need to sign in to each member account.
                  </p>
                  {/* Stated up-front rather than surfaced as a failure after
                      the customer clicks: AWS requires a person to enable this
                      and the platform genuinely cannot do it, so discovering it
                      as an error reads like the feature is broken. */}
                  <p className="mt-1 text-xs text-primary">
                    <strong>One-time AWS setup:</strong> StackSets trusted access must be enabled in your organization
                    (AWS Console → CloudFormation → StackSets, accept the prompt). Once per organization, not per
                    account - adding more accounts later needs nothing further.
                  </p>
                  {/* Phase 2 first, since it's the one-click path - with the
                      manual template still one click away for anyone who
                      hasn't granted (or doesn't want to grant) the StackSets
                      permission. */}
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <button
                      onClick={handleAutoDeploy}
                      disabled={autoDeploying}
                      className="rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
                    >
                      {autoDeploying ? 'Deploying…' : 'Deploy automatically'}
                    </button>
                    {!stackSetTemplate && (
                      <button
                        onClick={handleLoadStackSet}
                        disabled={stackSetLoading}
                        className="rounded-md border border-primary/30 bg-card px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/10 disabled:opacity-50 dark:border-primary/30 dark:bg-transparent dark:text-primary"
                      >
                        {stackSetLoading ? 'Generating…' : "I'll run it myself"}
                      </button>
                    )}
                  </div>

                  {autoDeployMessage && (
                    <div
                      className={`mt-2 rounded-md p-2 text-xs ${
                        autoDeployMessage.kind === 'error'
                          ? 'bg-rose-50 text-rose-800 dark:bg-rose-900/20 dark:text-rose-200'
                          : autoDeployMessage.kind === 'setup'
                          ? 'bg-amber-50 text-amber-900 dark:bg-amber-900/20 dark:text-amber-200'
                          : 'bg-card text-primary dark:bg-card dark:text-primary'
                      }`}
                    >
                      {autoDeployMessage.text}
                    </div>
                  )}

                  {autoDeployProgress && (
                    <div className="mt-2 rounded-md bg-card p-2 text-xs dark:bg-card">
                      <div className="font-medium text-foreground/80">
                        AWS operation: {autoDeployProgress.status}
                      </div>
                      {autoDeployProgress.instances.length > 0 && (
                        <ul className="mt-1 space-y-0.5">
                          {autoDeployProgress.instances.map((inst) => (
                            <li key={inst.accountId} className="flex justify-between gap-2 text-muted-foreground">
                              <span className="font-mono">{inst.accountId}</span>
                              <span>{inst.status}{inst.statusReason ? ` - ${inst.statusReason}` : ''}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}

                  {stackSetTemplate && (
                    <div className="mt-2 space-y-2">
                      <ArtifactCodeViewer cloudFormationTemplate={stackSetTemplate} />
                      <ol className="ml-4 list-decimal space-y-1 text-xs text-primary">
                        <li>AWS Console (management account) → CloudFormation → StackSets → Create StackSet</li>
                        <li>Choose "Upload a template file" and use the template above</li>
                        <li>Permissions: <strong>Service-managed permissions</strong></li>
                        <li>Deployment targets: <strong>Deploy to organizational units</strong>, pick the OU holding these accounts</li>
                        <li>Pick any single region (IAM is global), acknowledge IAM capabilities, submit</li>
                        <li>Come back here and use Verify on each account below</li>
                      </ol>
                    </div>
                  )}
                  {stackSetError && (
                    <p className="mt-2 text-xs text-rose-700 dark:text-rose-300">{stackSetError}</p>
                  )}
                </div>

                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm text-muted-foreground">
                    Found {hierarchyModal.accounts.length} sibling account(s). "Onboard" records each one using this
                    account's role name - each still needs that role to exist in its own account, which is what the
                    StackSet above does.
                  </p>
                  {hierarchyModal.accounts.some(
                    (a) => siblingOnboardState[a.accountId] !== 'done' && siblingOnboardState[a.accountId] !== 'awaiting-role'
                  ) && (
                    <button
                      onClick={handleOnboardAllSiblings}
                      disabled={Object.values(siblingOnboardState).some((s) => s === 'onboarding')}
                      title="Onboard every sibling below that already has the cross-account role deployed"
                      className="shrink-0 rounded-md bg-sky-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-sky-700 disabled:opacity-50"
                    >
                      {Object.values(siblingOnboardState).some((s) => s === 'onboarding') ? 'Onboarding all…' : 'Onboard all'}
                    </button>
                  )}
                </div>
                {hierarchyModal.accounts.map((acc) => {
                  const state = siblingOnboardState[acc.accountId];
                  const artifacts = siblingArtifacts[acc.accountId];
                  return (
                    <div key={acc.accountId} className="rounded-md border border-border p-3 text-sm dark:border-border">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="font-medium text-foreground">{acc.name}</div>
                          <div className="text-xs text-muted-foreground">{acc.accountId}</div>
                        </div>
                        {state === 'done' ? (
                          <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200">Onboarded</span>
                        ) : state === 'awaiting-role' ? (
                          <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800 dark:bg-amber-900 dark:text-amber-200">Role needed</span>
                        ) : (
                          <button
                            onClick={() => handleOnboardSibling(acc)}
                            disabled={state === 'onboarding'}
                            className="shrink-0 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-sky-600 hover:bg-sky-50 disabled:opacity-50 dark:border-border dark:bg-secondary dark:hover:bg-secondary"
                          >
                            {state === 'onboarding' ? 'Onboarding…' : 'Onboard'}
                          </button>
                        )}
                      </div>

                      {/* Previously a successful onboard call showed a green
                          "Onboarded" badge unconditionally, even though the
                          account has no role deployed yet and cannot actually
                          be used - misleading, and left the customer with no
                          way to get a deployable template for THIS account
                          without outside help. This renders the same
                          CloudFormation/Terraform/script the main wizard
                          already generates, just for this sibling's account
                          ID, plus a real Verify action once it's deployed. */}
                      {state === 'awaiting-role' && artifacts && (
                        <div className="mt-3 space-y-2">
                          <p className="text-xs text-muted-foreground">
                            Needs the role in AWS account <span className="font-mono">{acc.accountId}</span>. Use the
                            StackSet above to cover every member account at once, then click Verify here.
                          </p>
                          {/* Kept as a per-account fallback, collapsed by
                              default: the StackSet above is the intended path,
                              but an org that can't use StackSets (no trusted
                              access, or a single stray account outside the OU)
                              still needs a way through. */}
                          <details className="text-xs">
                            <summary className="cursor-pointer text-muted-foreground hover:text-foreground/80 dark:text-muted-foreground/70 dark:hover:text-foreground">
                              Or deploy this one account manually
                            </summary>
                            <div className="mt-2">
                              <ArtifactCodeViewer
                                cloudFormationTemplate={artifacts.cloudFormationTemplate}
                                terraformSnippet={artifacts.terraformSnippet}
                                onboardingScript={artifacts.onboardingScript}
                                policyDocument={artifacts.policyDocument}
                              />
                            </div>
                          </details>
                          <button
                            onClick={() => handleVerifySibling(acc)}
                            disabled={siblingVerifying[acc.accountId]}
                            className="w-full rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground/80 hover:bg-muted/30 disabled:opacity-50 dark:border-border dark:bg-secondary dark:text-muted-foreground dark:hover:bg-secondary"
                          >
                            {siblingVerifying[acc.accountId] ? 'Verifying…' : "I've deployed this - Verify Now"}
                          </button>
                        </div>
                      )}

                      {(state === 'error' || state === 'awaiting-role') && siblingErrors[acc.accountId] && (
                        <div className="mt-2 rounded-md bg-amber-50 p-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                          {siblingErrors[acc.accountId]}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <button
              onClick={() => setHierarchyModal(null)}
              className="mt-4 w-full rounded-md border border-border bg-card px-3 py-2 text-sm font-medium text-foreground/80 hover:bg-muted/30 dark:border-border dark:bg-secondary dark:text-muted-foreground dark:hover:bg-secondary"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Onboarding Wizard Modal */}
      {showWizard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-card p-4 shadow-xl dark:bg-card">
            <div className="flex items-center justify-between border-b border-border pb-4 dark:border-border">
              <h3 className="text-sm font-semibold text-foreground">
                Add Cloud Account
              </h3>
              <button
                onClick={resetWizard}
                className="text-muted-foreground/70 hover:text-muted-foreground dark:hover:text-foreground"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="mt-6">
              {policyDocument ? (
                <div className="space-y-4">
                  <div className="rounded-lg bg-emerald-50 p-4 dark:bg-emerald-900/20">
                    <h4 className="text-sm font-medium text-emerald-900 dark:text-emerald-200">Account saved</h4>
                    <p className="mt-1 text-sm text-emerald-700 dark:text-emerald-300">
                      Marked "pending verification" - we haven't tested this connection against the real
                      cloud account yet. Hand the policy below to whoever administers it so the access can
                      be granted.
                    </p>
                  </div>
                  {overlapWarnings.length > 0 && (
                    <div className="rounded-md bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                      <p className="font-medium">Possible double-counting</p>
                      <ul className="mt-1 list-disc space-y-1 pl-4">
                        {overlapWarnings.map((w, i) => <li key={i}>{w}</li>)}
                      </ul>
                    </div>
                  )}
                  {manifestInfo && (
                    <div className={`rounded-md p-3 text-xs ${manifestInfo.signed ? 'bg-muted/30 text-muted-foreground dark:bg-card/40 dark:text-muted-foreground' : 'bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-200'}`}>
                      {manifestInfo.signed ? (
                        <>
                          <span className="font-medium">Manifest signed ✓</span> — a tamper-evident record of exactly
                          what was generated for this onboarding is stored.
                          {manifestInfo.hash && (
                            <div className="mt-1 font-mono text-[11px] opacity-80">
                              SHA-256: {manifestInfo.hash.slice(0, 16)}…{manifestInfo.hash.slice(-8)}
                            </div>
                          )}
                          {manifestInfo.signedAt && (
                            <div className="mt-0.5 opacity-80">Signed at {new Date(manifestInfo.signedAt).toLocaleString()}</div>
                          )}
                        </>
                      ) : (
                        'Manifest could not be signed yet (signing key unavailable) - the account was still created without one.'
                      )}
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-foreground/80">
                      {cloudFormationTemplate || terraformSnippet || onboardingScript ? 'Onboarding artifacts' : 'Least-privilege policy document'}
                    </label>
                    <ArtifactCodeViewer
                      cloudFormationTemplate={cloudFormationTemplate}
                      terraformSnippet={terraformSnippet}
                      onboardingScript={onboardingScript}
                      policyDocument={policyDocument}
                    />
                  </div>
                  <div className="flex justify-end border-t border-border pt-4 dark:border-border">
                    <button
                      onClick={resetWizard}
                      className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      Done
                    </button>
                  </div>
                </div>
              ) : !selectedProvider ? (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Select a cloud provider to begin the onboarding process:
                  </p>
                  <div className="grid gap-4 md:grid-cols-3">
                    {[CloudProvider.AWS, CloudProvider.AZURE, CloudProvider.GCP].map((provider) => (
                      <button
                        key={provider}
                        onClick={() => setSelectedProvider(provider)}
                        className="flex flex-col items-center gap-3 rounded-lg border-2 border-border p-4 hover:border-primary/30 hover:bg-primary/10 focus:outline-none focus:ring-2 focus:ring-ring dark:border-border dark:hover:border-primary/50 dark:hover:bg-secondary"
                      >
                        <span
                          className="flex h-14 w-14 items-center justify-center rounded-full"
                          style={{ backgroundColor: getProviderBackgroundTint(provider) }}
                        >
                          <CloudProviderIcon provider={provider} size={30} />
                        </span>
                        <span className="font-medium text-foreground">
                          {provider.toUpperCase()}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="rounded-lg bg-primary/10 p-4 dark:bg-primary/10">
                    <h4 className="text-sm font-medium text-primary">
                      {selectedProvider.toUpperCase()} Onboarding Wizard
                    </h4>
                    <p className="mt-2 text-sm text-primary">
                      {WIZARD_COPY[selectedProvider]}
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground/80">
                        Account Name
                      </label>
                      <input
                        type="text"
                        value={accountName}
                        onChange={(e) => setAccountName(e.target.value)}
                        placeholder="e.g., Production AWS"
                        className="mt-1 block w-full rounded-md border border-border px-3 py-2 shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring dark:border-border dark:bg-secondary dark:text-white"
                      />
                    </div>

                    {FIELD_DEFS[selectedProvider].map((field) =>
                      field.type === 'checkbox' ? (
                        <div key={field.key} className="sm:col-span-2">
                          <label className="flex items-start gap-2">
                            <input
                              type="checkbox"
                              checked={fieldValues[field.key] === 'true'}
                              onChange={(e) =>
                                setFieldValues((prev) => ({ ...prev, [field.key]: e.target.checked ? 'true' : '' }))
                              }
                              className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-ring dark:border-border"
                            />
                            <span className="text-sm font-medium text-foreground/80">{field.label}</span>
                          </label>
                          {field.help && (
                            <p className="mt-1 ml-6 text-xs text-muted-foreground">{field.help}</p>
                          )}
                        </div>
                      ) : (
                        <div key={field.key}>
                          <label className="block text-sm font-medium text-foreground/80">
                            {field.label}
                          </label>
                          <input
                            type={field.type || 'text'}
                            value={fieldValues[field.key] || ''}
                            onChange={(e) => setFieldValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                            placeholder={field.placeholder}
                            autoComplete={field.type === 'password' ? 'new-password' : 'off'}
                            className="mt-1 block w-full rounded-md border border-border px-3 py-2 shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring dark:border-border dark:bg-secondary dark:text-white"
                          />
                        </div>
                      )
                    )}
                  </div>

                  {submitError && (
                    <div className="rounded-md bg-rose-50 p-3 text-sm text-rose-700 dark:bg-rose-900/20 dark:text-rose-300">
                      {submitError}
                    </div>
                  )}

                  {/* Preflight check (Requirement 119/172) - catches a missing
                      permission grant BEFORE the account is marked onboarded,
                      instead of only discovering it days later on a real scan. */}
                  <div className="rounded-md border border-border p-3 dark:border-border">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-foreground/80">Check permissions (optional, recommended)</span>
                      <button
                        onClick={handleCheckPreflight}
                        disabled={preflightChecking}
                        className="rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground/80 hover:bg-muted/30 disabled:opacity-50 dark:border-border dark:bg-secondary dark:text-muted-foreground dark:hover:bg-secondary"
                      >
                        {preflightChecking ? 'Checking…' : 'Check Permissions'}
                      </button>
                    </div>
                    {preflightResult && (
                      <div className={`mt-2 rounded-md p-2 text-xs ${preflightResult.notAvailable ? 'bg-muted/30 text-muted-foreground dark:bg-card/40 dark:text-muted-foreground/70' : preflightResult.ok ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-200' : 'bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-200'}`}>
                        <p>{preflightResult.message}</p>
                        {preflightResult.results.length > 0 && (
                          <ul className="mt-1 space-y-0.5">
                            {preflightResult.results.map((r, i) => (
                              <li key={i}>{r.allowed ? '✓' : '✗'} {r.label || r.action}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end gap-3 border-t border-border pt-4 dark:border-border">
                    <button
                      onClick={() => setSelectedProvider(null)}
                      disabled={submitting}
                      className="rounded-md border border-border bg-card px-4 py-2 text-sm font-medium text-foreground/80 hover:bg-muted/30 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 dark:border-border dark:bg-secondary dark:text-muted-foreground dark:hover:bg-secondary"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={submitting || (preflightResult !== null && !preflightResult.notAvailable && !preflightResult.ok)}
                      title={preflightResult && !preflightResult.notAvailable && !preflightResult.ok ? 'Fix the missing permissions above before onboarding - the server will reject this the same way' : undefined}
                      className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                    >
                      {submitting ? 'Saving…' : 'Start Onboarding'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
